import { computed, inject, provide, toValue } from "vue";
import type { ComputedRef, InjectionKey, MaybeRefOrGetter } from "vue";
import {
	priorityImageAttrs,
	toImagePriority,
	type ImagePriority,
	type PriorityImageAttrs
} from "~/layers/base/utils/imagePriority";

const BLOK_PRIORITY_KEY: InjectionKey<ComputedRef<ImagePriority>> =
	Symbol("blokPriority");

/**
 * Sets how the images of every blok rendered below the caller load: `true`
 * for first-screen content, `false` for the rest, or an explicit level.
 * Page-level renderers set it per top-level blok (see `BlokPriorityScope`); a
 * nested scope overrides its parent, which is how carousel slides opt out.
 */
export const provideBlokPriority = (
	priority: MaybeRefOrGetter<boolean | ImagePriority>
): void => {
	provide(
		BLOK_PRIORITY_KEY,
		computed(() => toImagePriority(toValue(priority)))
	);
};

/**
 * How the calling blok's images load. Lazy outside any scope: a blok whose
 * position on the page is unknown (a category-grid ad, a tab) is treated as
 * below the fold.
 */
export const useBlokPriority = (): ComputedRef<ImagePriority> =>
	inject(BLOK_PRIORITY_KEY, null) ?? computed(() => "lazy");

/** `useBlokPriority()` as NuxtImg loading attributes, for `v-bind`. */
export const useBlokImagePriority = (): ComputedRef<PriorityImageAttrs> => {
	const priority = useBlokPriority();
	return computed(() => priorityImageAttrs(priority.value));
};
